import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { pickPalette } from "@picnic-field/renderer/appearance";
import type { Appearance, PaletteLike } from "@picnic-field/renderer/appearance";
import { useBootDeps } from "../lib/boot/deps";
import type { PreviewLike } from "../lib/preview";

/**
 * 3D 미리보기 캔버스(C-02) — 돌려 보기만, 색 편집 없음(SR-MDF-06 · SCR FS-02-02).
 * 미리보기가 실패해도(WebGL · GLB) 올리기를 막지 않는다: 자리표시로 바꾸고 안내 한 줄.
 * data-state: loading | ready | fallback — E2E 가 첫 프레임을 기다리는 데 쓴다.
 */
export type PreviewState = "loading" | "ready" | "fallback";

export interface ModelPreviewProps {
  glbUrl: string | null;
  appearance: Appearance | null;
  palette: PaletteLike | null;
  label: string;
  interactive?: boolean;
  autoRotate?: boolean;
  quality?: "high" | "low";
  className?: string;
  /** 자리표시·안내(선택) */
  fallback?: ReactNode;
  overlay?: ReactNode;
  onState?: (s: PreviewState) => void;
}

export function ModelPreview({
  glbUrl,
  appearance,
  palette: paletteIn,
  label,
  interactive = true,
  autoRotate = true,
  quality = "high",
  className,
  fallback,
  overlay,
  onState,
}: ModelPreviewProps) {
  const { createPreview } = useBootDeps();
  // 작품 제 팔레트 이름으로(2026-09-30 팔레트 버전 전환) — 묶음이면 appearance.palette 의 것, 하나면 그대로(이름 대조는 렌더러)
  const palette = pickPalette(paletteIn, appearance?.palette ?? null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const handleRef = useRef<PreviewLike | null>(null);
  const [state, setState] = useState<PreviewState>(glbUrl ? "loading" : "fallback");
  const [tinted, setTinted] = useState(0);
  // 색·팔레트는 첫 생성에 쓰고, 바뀌면 setAppearance 로만 반영(GLB 재로드 없음)
  const look = useRef({ appearance, palette });
  look.current = { appearance, palette };
  const onStateRef = useRef(onState);
  onStateRef.current = onState;

  useEffect(() => {
    onStateRef.current?.(state);
  }, [state]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!glbUrl || !canvas) {
      setState("fallback");
      return;
    }
    let cancelled = false;
    let handle: PreviewLike | null = null;
    setState("loading");
    createPreview(canvas, {
      glbUrl,
      appearance: look.current.appearance,
      palette: look.current.palette,
      interactive,
      autoRotate,
      quality,
    })
      .then(async (h) => {
        handle = h;
        if (cancelled) return h.dispose();
        handleRef.current = h;
        const info = await h.ready;
        if (cancelled) return;
        setTinted(info.tintedMeshes);
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("fallback");
      });
    return () => {
      cancelled = true;
      handleRef.current = null;
      handle?.dispose();
    };
  }, [createPreview, glbUrl, interactive, autoRotate, quality]);

  useEffect(() => {
    handleRef.current?.setAppearance(appearance, palette);
  }, [appearance, palette]);

  return (
    <div
      className={["pf-preview", `pf-preview--${state}`, className].filter(Boolean).join(" ")}
      data-state={state}
      data-tinted={tinted}
      data-testid="model-preview"
    >
      <canvas
        ref={canvasRef}
        className="pf-preview__canvas"
        role="img"
        aria-label={label}
        hidden={state === "fallback"}
      />
      {state === "fallback" && (
        <div className="pf-preview__fallback" aria-hidden="true">
          <span className="pf-preview__glyph">🧩</span>
        </div>
      )}
      {state === "fallback" && fallback}
      {state === "ready" && overlay}
    </div>
  );
}
